import { Box, Button, Checkbox, FormControlLabel, Paper } from '@mui/material';
import { Fragment } from 'react';
import { useCommonTrailStyles } from '../trail/trailStyles';
import {GenericConstant} from '../../utils/GenericConstant';
export type ColumnVisibility = Record<string, boolean>;

export interface CustomizeColumnsPanelProps {
    show: boolean;
    columnVisibility: ColumnVisibility;
    onColumnChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onResetToDefault: () => void;
    saveColumnPreferences: () => void;
    buttonHeight?: string;
    buttonPadding?: string;
    /** Map of column keys to display labels */
    columnLabelMap?: Record<string, string>;
    /** Array of column keys that should be marked as required and disabled */
    requiredColumns?: string[];
    /** Column key to always place at the end (e.g., 'Action') */
    lastColumnKey?: string;
    /** Paper elevation (default: 0) */
    elevation?: number;
}

const defaultColumnLabelMap: Record<string, string> = {
    'Material ID': 'Material ID',
    'Plants to Harvest': 'Plants to Harvest',
    'Current Status': 'Status'
};

export function CustomizeColumnsPanel({
    show,
    columnVisibility,
    onColumnChange,
    onResetToDefault,
    saveColumnPreferences,
    buttonHeight = '40px',
    buttonPadding = '10px 12px',
    columnLabelMap = defaultColumnLabelMap,
    requiredColumns = ['MatID', 'Material ID', 'Harvests'],
    lastColumnKey = 'Actions',
    elevation = 4,
}: CustomizeColumnsPanelProps) {
    const classes = useCommonTrailStyles();
    
    if (!show) return null;

    const getColumnLabel = (key: string): string => columnLabelMap[key] || key;

    const columns = Object.entries(columnVisibility);
    
    // Separate first, last, and middle columns
    const firstColumn = columns.length > 0 ? [columns[0]] : [];
    const lastColumn = columns.find(([key]) => key === lastColumnKey);
    const middleColumns = columns.filter(([key], index) => 
        key !== lastColumnKey && index !== 0
    );

    // Sort middle columns alphabetically by display label
    const sortedMiddleColumns = [...middleColumns].sort(([a], [b]) =>
        getColumnLabel(a).localeCompare(getColumnLabel(b), undefined, { sensitivity: 'base' })
    );
    
    const endColumns = lastColumn ? [lastColumn] : [];

    const renderColumnItem = (key: string, value: boolean, isRequired = false) => (
        <FormControlLabel 
            key={key}
            className={isRequired ? classes.columnItemRequired : classes.columnItemOptional}
            control={
                <Checkbox 
                    checked={Boolean(value)} 
                    name={key} 
                    disableRipple
                    onChange={onColumnChange}
                    disabled={isRequired}
                    sx={{ ...GenericConstant.CHECKBOXSTYLE, padding: '6px', '& .MuiSvgIcon-root': { fontSize: 20 } }}
                    checkedIcon={<Box component="img" alt="" src={"icons/CheckBox.svg"} sx={{ width: 20, height: 20, display: 'block' }} />}
                />
            } 
            label={isRequired ? (
                <Box className={classes.columnLabelBox}>
                    <span>{getColumnLabel(key)}</span>
                    <span className={classes.requiredLabel}>Required</span>
                </Box>
            ) : getColumnLabel(key)}
        />
    );

    return (
        <Fragment>
            <Box
                onClick={saveColumnPreferences}
                className={classes.customizeColumnsBackdrop}
            />
        <Paper elevation={elevation} className={classes.customizeColumnsPaper}
            onClick={(e) => e.stopPropagation()}>
            <Box className={classes.customizeColumnsContent}>
                {firstColumn.map(([key, value]) => renderColumnItem(key, value, requiredColumns.includes(key)))}
                {sortedMiddleColumns.map(([key, value]) => renderColumnItem(key, value, requiredColumns.includes(key)))}
                {endColumns.map(([key, value]) => renderColumnItem(key, value, true))}
            </Box>

            <Box className={classes.customizeColumnsFooter}>
                <Button 
                    variant="outlined" 
                    disableRipple
                    onClick={onResetToDefault} 
                    className={`${classes.customizeColumnResetButton}`}
                    sx={{ height: buttonHeight, padding: buttonPadding }}
                >
                    Reset to Default
                </Button>
                <Button  
                    disableRipple
                    variant="contained"
                    onClick={saveColumnPreferences}
                    className={classes.customizeColumnSaveButton}
                    sx={{ height: buttonHeight, padding: buttonPadding }}
                >
                    Save
                </Button>
            </Box>
        </Paper>
        </Fragment>
    );
}
